// Setting → Global Menu: เจ้าของข้อมูลเมนู 3 ขีดของทุกระบบในเครือ
//
// ระบบปลายทางไม่ต้องยิงมาถามทุกครั้งที่โหลดหน้า: แปะสคริปต์ที่ฝังเมนูมาแล้ว
// หรือรับสำเนาผ่านปุ่ม Push ไปเก็บในระบบตัวเอง
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { GlobalMenuGroup, GlobalMenuItem, GlobalMenuTarget } from "@contracts/api";
import { cn } from "@shared/lib/utils";
import {
  addGroup,
  addItem,
  addTarget,
  deleteGroup,
  deleteItem,
  deleteTarget,
  fetchGroups,
  fetchItems,
  fetchTargets,
  gmKeys,
  pushMenu,
  renameGroup,
  updateItem,
  updateTarget,
} from "../api/global-menu-api";
import { buildPrompt, type PromptKind } from "../lib/prompts";

export function GlobalMenuPage() {
  const qc = useQueryClient();
  const { data: rawItems, isPending } = useQuery({ queryKey: gmKeys.items, queryFn: fetchItems });
  const { data: groups } = useQuery({ queryKey: gmKeys.groups, queryFn: fetchGroups });
  const items = orderForDisplay(rawItems ?? []);
  const { data: targets, isPending: targetsPending } = useQuery({
    queryKey: gmKeys.targets,
    queryFn: fetchTargets,
  });
  const [msg, setMsg] = useState("");
  const [kind, setKind] = useState<PromptKind>("script");

  /*
   * snapshot = ชุดที่แถบซ้ายของ My Job กับเมนู 3 ขีดอ่าน — คนละ key กับรายการในหน้านี้
   * ไม่ล้างด้วย จะแก้ชื่อกลุ่มเสร็จแล้วกลับไปหน้า My Job ยังเห็นชื่อเดิมค้างอยู่
   */
  const reloadItems = () => {
    void qc.invalidateQueries({ queryKey: gmKeys.items });
    void qc.invalidateQueries({ queryKey: gmKeys.snapshot });
  };
  const reloadGroups = () => {
    void qc.invalidateQueries({ queryKey: gmKeys.groups });
    // ลบกลุ่มแล้วสมาชิกกลายเป็นเมนูหลัก — รายการเมนูจึงเปลี่ยนตามไปด้วยเสมอ
    void qc.invalidateQueries({ queryKey: gmKeys.items });
    void qc.invalidateQueries({ queryKey: gmKeys.snapshot });
  };
  const reloadTargets = () => qc.invalidateQueries({ queryKey: gmKeys.targets });

  const push = useMutation({
    mutationFn: pushMenu,
    onMutate: () => setMsg(""),
    onSuccess: (d) => {
      const ok = d.results.filter((x) => x.ok).length;
      const total = d.results.filter((x) => x.ok !== null).length;
      setMsg(`push ${d.count} เมนู · สำเร็จ ${ok}/${total} ระบบ (version ${d.version})`);
      void reloadTargets();
    },
    onError: () => setMsg("เชื่อมต่อ server ไม่ได้"),
  });

  return (
    <>
      <div className="content-head">
        <div>
          <div className="breadcrumb">Setting / Global Menu</div>
          <h1>Global Menu</h1>
        </div>
        <button
          type="button"
          className="btn-primary inline-btn"
          disabled={push.isPending}
          onClick={() => push.mutate()}
        >
          {push.isPending ? "กำลัง push…" : "↗ Push ไปทุกระบบ"}
        </button>
      </div>

      <div className="panel gm-panel">
        <h3>รายการเมนู</h3>
        <p className="muted small-note">
          ชื่อเมนู = ชื่อโปรเจค · ลากลำดับด้วยปุ่มลูกศร · ปิดไว้ = ไม่ส่งไปให้ระบบอื่น ·
          ช่องแรกคือกลุ่ม — ไม่เลือกกลุ่ม = เป็นเมนูหลัก
        </p>

        <GroupManager groups={groups ?? []} items={items} onChanged={reloadGroups} />

        <div className="gm-list">
          {isPending ? (
            <p className="muted">กำลังโหลด…</p>
          ) : !items.length ? (
            <p className="muted small-note">ยังไม่มีเมนู — เพิ่มด้านล่าง</p>
          ) : (
            items.map((m, i) => (
              <ItemRow
                key={m.id}
                item={m}
                groups={groups ?? []}
                first={i === 0}
                last={i === items.length - 1}
                onMove={async (dir) => {
                  const j = i + dir;
                  if (j < 0 || j >= items.length) return;
                  /*
                   * สลับค่า position ของจริงระหว่างสองแถว ไม่ใช่เอาลำดับที่แสดงไปเขียนทับ
                   * — พอมีกลุ่ม ลำดับที่แสดงไม่เท่ากับ position อีกต่อไป (กลุ่มดึงสมาชิก
                   * มาอยู่ติดกัน) เขียนด้วยเลขลำดับที่แสดงจะทำให้ของทั้งรายการเลื่อนมั่ว
                   */
                  await Promise.all([
                    updateItem(items[i].id, { position: items[j].position }),
                    updateItem(items[j].id, { position: items[i].position }),
                  ]);
                  void reloadItems();
                }}
                onSaved={reloadItems}
              />
            ))
          )}
        </div>
        <AddItemForm onAdded={reloadItems} />
        <p className="muted small-note">{msg}</p>
      </div>

      <div className="panel gm-panel">
        <h3>ระบบปลายทาง</h3>
        <p className="muted small-note">
          ระบบที่มี endpoint รับสำเนาเมนู — กด Push แล้วจะส่งไปให้ทุกตัวในรายการนี้ ระบบที่ยังไม่มี
          ให้ใช้ Prompt ด้านล่างไปบอกทีมของระบบนั้น
        </p>
        <div className="gm-list">
          {targetsPending ? (
            <p className="muted">กำลังโหลด…</p>
          ) : !targets?.length ? (
            <p className="muted small-note">
              ยังไม่มีระบบปลายทาง — ระบบที่แปะสคริปต์ไว้ไม่ต้องใส่ที่นี่
            </p>
          ) : (
            targets.map((t) => <TargetRow key={t.id} target={t} onSaved={reloadTargets} />)
          )}
        </div>
        <AddTargetForm onAdded={reloadTargets} />
      </div>

      <div className="panel gm-panel">
        <h3>Prompt สำหรับให้ระบบอื่นเชื่อมเมนูนี้</h3>
        <p className="muted small-note">
          เลือกวิธี แล้วคัดลอกไปวางในโปรเจคปลายทาง (ใช้เป็นคำสั่งให้ Claude Code ของระบบนั้นได้เลย)
        </p>
        <div className="gm-prompt-tabs">
          <button
            type="button"
            className={cn("btn-outline", "btn-sm", kind === "script" && "active")}
            onClick={() => setKind("script")}
          >
            แบบที่ 1 — แปะสคริปต์บรรทัดเดียว
          </button>
          <button
            type="button"
            className={cn("btn-outline", "btn-sm", kind === "push" && "active")}
            onClick={() => setKind("push")}
          >
            แบบที่ 2 — รับสำเนาผ่าน Push
          </button>
        </div>
        <PromptBox kind={kind} items={items ?? []} />
      </div>
    </>
  );
}

/* ───────── รายการเมนู ───────── */

function ItemRow({
  item,
  groups,
  first,
  last,
  onMove,
  onSaved,
}: {
  item: GlobalMenuItem;
  groups: GlobalMenuGroup[];
  first: boolean;
  last: boolean;
  onMove: (dir: -1 | 1) => void;
  onSaved: () => void;
}) {
  // แก้แล้วบันทึกตอน blur/Enter เหมือนของเดิม (ผูกกับ event change ของ input)
  const [label, setLabel] = useState(item.label);
  const [description, setDescription] = useState(item.description);
  const [url, setUrl] = useState(item.url);

  const save = useMutation({
    mutationFn: () =>
      updateItem(item.id, {
        label: label.trim(),
        description: description.trim(),
        url: url.trim(),
      }),
    onSuccess: onSaved,
  });
  const toggle = useMutation({
    mutationFn: () => updateItem(item.id, { enabled: !item.enabled }),
    onSuccess: onSaved,
  });
  const remove = useMutation({ mutationFn: () => deleteItem(item.id), onSuccess: onSaved });
  // ย้ายเข้า/ออกกลุ่ม — บันทึกทันทีที่เลือก ไม่ต้องรอ blur เหมือนช่องพิมพ์
  const move = useMutation({
    mutationFn: (groupId: string) => updateItem(item.id, { groupId }),
    onSuccess: onSaved,
  });

  const commit = () => save.mutate();

  return (
    <div className={cn("gm-row", !item.enabled && "is-off")}>
      <span className="gm-ord">
        <button
          type="button"
          className="icon-btn gm-up"
          title="เลื่อนขึ้น"
          disabled={first}
          onClick={() => onMove(-1)}
        >
          ↑
        </button>
        <button
          type="button"
          className="icon-btn gm-down"
          title="เลื่อนลง"
          disabled={last}
          onClick={() => onMove(1)}
        >
          ↓
        </button>
      </span>
      <select
        className="camp-select gm-f-group"
        title="กลุ่มของเมนูนี้"
        value={item.groupId ?? ""}
        disabled={move.isPending}
        onChange={(e) => move.mutate(e.target.value)}
      >
        <option value="">— เมนูหลัก —</option>
        {groups.map((g) => (
          <option key={g.id} value={g.id}>
            {g.name}
          </option>
        ))}
      </select>
      <span className="grow">
        <input
          type="text"
          className="gm-f-label"
          value={label}
          maxLength={80}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={commit}
        />
        <input
          type="text"
          className="gm-f-desc"
          value={description}
          placeholder="คำอธิบายสั้น"
          maxLength={140}
          onChange={(e) => setDescription(e.target.value)}
          onBlur={commit}
        />
        <input
          type="text"
          className="gm-f-url"
          value={url}
          maxLength={300}
          onChange={(e) => setUrl(e.target.value)}
          onBlur={commit}
        />
      </span>
      <button
        type="button"
        className={cn("switch", item.enabled && "on")}
        role="switch"
        aria-checked={item.enabled}
        aria-label="เปิดใช้"
        disabled={toggle.isPending}
        onClick={() => toggle.mutate()}
      >
        <span className="switch-dot" />
      </button>
      <button
        type="button"
        className="icon-btn gm-del"
        title="ลบเมนูนี้"
        onClick={() => {
          if (confirm("ลบเมนูนี้?")) remove.mutate();
        }}
      >
        ✕
      </button>
    </div>
  );
}

/**
 * เรียงให้สมาชิกของกลุ่มเดียวกันติดกัน โดยกลุ่มไปอยู่ตำแหน่งของสมาชิกตัวแรก
 *
 * กติกาเดียวกับฝั่ง server (global-menu.service.ts) — หน้านี้จึงแสดงลำดับเดียวกับที่
 * ลิ้นชักเมนูจะออกมาจริง ไม่ใช่ลำดับที่ต้องมาเดาเอาเองว่าพอกดกลุ่มแล้วจะสลับไปยังไง
 */
function orderForDisplay(items: GlobalMenuItem[]): GlobalMenuItem[] {
  const firstOf = new Map<string, number>();
  for (const m of items) {
    if (!m.groupId) continue;
    const at = firstOf.get(m.groupId);
    if (at === undefined || m.position < at) firstOf.set(m.groupId, m.position);
  }
  const key = (m: GlobalMenuItem) =>
    m.groupId ? (firstOf.get(m.groupId) ?? m.position) : m.position;
  return [...items].sort((a, b) => key(a) - key(b) || a.position - b.position);
}

/** สร้าง / เปลี่ยนชื่อ / ลบกลุ่ม — การย้ายเมนูเข้ากลุ่มทำที่ช่องแรกของแต่ละแถว */
function GroupManager({
  groups,
  items,
  onChanged,
}: {
  groups: GlobalMenuGroup[];
  items: GlobalMenuItem[];
  onChanged: () => void;
}) {
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");

  const add = useMutation({
    mutationFn: () => addGroup(name.trim()),
    onSuccess: () => {
      setName("");
      onChanged();
    },
    onError: () => setMsg("สร้างกลุ่มไม่สำเร็จ"),
  });

  const count = (id: string) => items.filter((m) => m.groupId === id).length;

  return (
    <div className="gm-groups">
      <div className="gm-group-chips">
        {groups.length ? (
          groups.map((g) => (
            <GroupChip key={g.id} group={g} members={count(g.id)} onChanged={onChanged} />
          ))
        ) : (
          <span className="muted small-note">ยังไม่มีกลุ่ม — เมนูทั้งหมดอยู่ชั้นบนสุด</span>
        )}
      </div>
      <div className="gm-group-add">
        <input
          type="text"
          placeholder="ชื่อกลุ่มใหม่ เช่น Media Tools"
          maxLength={60}
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && name.trim()) add.mutate();
          }}
        />
        <button
          type="button"
          className="btn-outline btn-sm"
          disabled={add.isPending || !name.trim()}
          onClick={() => {
            setMsg("");
            add.mutate();
          }}
        >
          + สร้างกลุ่ม
        </button>
        {msg && <span className="muted small-note">{msg}</span>}
      </div>
    </div>
  );
}

function GroupChip({
  group,
  members,
  onChanged,
}: {
  group: GlobalMenuGroup;
  members: number;
  onChanged: () => void;
}) {
  const [name, setName] = useState(group.name);

  const rename = useMutation({
    mutationFn: () => renameGroup(group.id, name.trim() || group.name),
    onSuccess: onChanged,
  });
  const remove = useMutation({ mutationFn: () => deleteGroup(group.id), onSuccess: onChanged });

  return (
    <span className="gm-group-chip">
      <input
        type="text"
        value={name}
        maxLength={60}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => name.trim() !== group.name && rename.mutate()}
      />
      <span className="muted">{members} เมนู</span>
      <button
        type="button"
        className="icon-btn"
        title="ลบกลุ่มนี้"
        disabled={remove.isPending}
        onClick={() => {
          // บอกจำนวนสมาชิกในคำถาม เพราะผลที่เกิดกับเมนูคือสิ่งที่คนกดอยากรู้ ไม่ใช่ชื่อกลุ่ม
          const note = members
            ? `ลบกลุ่ม “${group.name}”? เมนู ${members} รายการจะกลับไปเป็นเมนูหลัก (ไม่ถูกลบ)`
            : `ลบกลุ่ม “${group.name}”?`;
          if (confirm(note)) remove.mutate();
        }}
      >
        ✕
      </button>
    </span>
  );
}

function AddItemForm({ onAdded }: { onAdded: () => void }) {
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [msg, setMsg] = useState("");

  const add = useMutation({
    mutationFn: () =>
      addItem({ label: label.trim(), description: description.trim(), url: url.trim() }),
    onSuccess: () => {
      setLabel("");
      setDescription("");
      setUrl("");
      onAdded();
    },
    onError: () => setMsg("เพิ่มไม่สำเร็จ"),
  });

  return (
    <>
      <div className="gm-add">
        <input
          type="text"
          placeholder="ชื่อโปรเจค"
          maxLength={80}
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <input
          type="text"
          placeholder="คำอธิบายสั้น"
          maxLength={140}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
        <input
          type="text"
          placeholder="https://…"
          maxLength={300}
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <button
          type="button"
          className="btn-primary btn-sm"
          disabled={add.isPending}
          onClick={() => {
            setMsg("");
            if (!label.trim() || !url.trim()) {
              setMsg("ต้องมีชื่อโปรเจคและ URL");
              return;
            }
            add.mutate();
          }}
        >
          + เพิ่มเมนู
        </button>
      </div>
      <p className="muted small-note">{msg}</p>
    </>
  );
}

/* ───────── ระบบปลายทาง ───────── */

function TargetRow({ target, onSaved }: { target: GlobalMenuTarget; onSaved: () => void }) {
  const [name, setName] = useState(target.name);
  const [pushUrl, setPushUrl] = useState(target.pushUrl);

  const save = useMutation({
    mutationFn: () => updateTarget(target.id, { name: name.trim(), pushUrl: pushUrl.trim() }),
  });
  const remove = useMutation({ mutationFn: () => deleteTarget(target.id), onSuccess: onSaved });

  const status = !target.pushUrl
    ? "ยังไม่ได้ตั้ง endpoint รับ — push ข้ามระบบนี้"
    : target.lastPushStatus
      ? target.lastPushStatus +
        (target.lastPushAt ? " · " + new Date(target.lastPushAt).toLocaleString("th-TH") : "")
      : "ยังไม่เคย push";

  return (
    <div className="gm-row">
      <span className="grow">
        <input
          type="text"
          className="gt-f-name"
          value={name}
          maxLength={80}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => save.mutate()}
        />
        <input
          type="text"
          className="gt-f-url"
          value={pushUrl}
          placeholder="endpoint รับสำเนา"
          maxLength={300}
          onChange={(e) => setPushUrl(e.target.value)}
          onBlur={() => save.mutate()}
        />
        <span className="provider-status muted">
          {status}
          {target.hasSecret ? " · มี secret" : ""}
        </span>
      </span>
      <button
        type="button"
        className="icon-btn gt-del"
        title="เอาระบบนี้ออก"
        onClick={() => {
          if (confirm("เอาระบบนี้ออกจากรายการ push?")) remove.mutate();
        }}
      >
        ✕
      </button>
    </div>
  );
}

function AddTargetForm({ onAdded }: { onAdded: () => void }) {
  const [name, setName] = useState("");
  const [pushUrl, setPushUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [msg, setMsg] = useState("");

  const add = useMutation({
    mutationFn: () =>
      addTarget({ name: name.trim(), pushUrl: pushUrl.trim(), secret: secret.trim() }),
    onSuccess: () => {
      setName("");
      setPushUrl("");
      setSecret("");
      onAdded();
    },
    onError: () => setMsg("เพิ่มไม่สำเร็จ"),
  });

  return (
    <>
      <div className="gm-add">
        <input
          type="text"
          placeholder="ชื่อระบบ"
          maxLength={80}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <input
          type="text"
          placeholder="https://…/api/global-menu (endpoint รับ)"
          maxLength={300}
          value={pushUrl}
          onChange={(e) => setPushUrl(e.target.value)}
        />
        <input
          type="text"
          placeholder="secret (ไม่บังคับ)"
          maxLength={120}
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
        />
        <button
          type="button"
          className="btn-primary btn-sm"
          disabled={add.isPending}
          onClick={() => {
            setMsg("");
            if (!name.trim()) {
              setMsg("ต้องมีชื่อระบบ");
              return;
            }
            add.mutate();
          }}
        >
          + เพิ่มระบบ
        </button>
      </div>
      <p className="muted small-note">{msg}</p>
    </>
  );
}

/* ───────── Prompt ───────── */

function PromptBox({ kind, items }: { kind: PromptKind; items: GlobalMenuItem[] }) {
  const [note, setNote] = useState("");
  const text = buildPrompt(kind, items, location.origin);

  return (
    <>
      <textarea className="gm-prompt" readOnly rows={16} value={text} />
      <div className="gm-add">
        <button
          type="button"
          className="btn-outline btn-sm"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(text);
              setNote("คัดลอกแล้ว");
            } catch {
              setNote("กด Cmd+C เพื่อคัดลอก");
            }
            setTimeout(() => setNote(""), 2500);
          }}
        >
          คัดลอก Prompt
        </button>
        <span className="muted small-note">{note}</span>
      </div>
    </>
  );
}
